import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';
import AnalyzerHeader from './AnalyzerHeader';
import ScoreCard from './ScoreCard';
import { algorithmCatalog, ALGO_HEALTH } from './algorithmCatalog';
import NeighborDataChipConnected from './NeighborDataChipConnected';
import { useNeighborData } from './NeighborDataContext';

const severityColor = (sev) => {
  if (sev >= 0.75) return 'error';
  if (sev >= 0.5) return 'warning';
  if (sev >= 0.25) return 'info';
  return 'default';
};

const HealthPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const catalog = algorithmCatalog[ALGO_HEALTH];
  const { mergeFreshness } = useNeighborData();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getHealthAnalysis();
      setResult(data);
      if (data?.data_freshness) {
        mergeFreshness(data.data_freshness);
      }
      const issues = data?.issues?.length || 0;
      const missing = data?.data_freshness?.devices_missing_data || 0;
      enqueueSnackbar(
        issues === 0
          ? missing > 0
            ? `Health: no issues detected (but ${missing} device(s) missing neighbour data — long-path hints are partial).`
            : 'Health: no issues detected.'
          : `Health: ${issues} issue(s) reported.`,
        { variant: issues === 0 && missing === 0 ? 'success' : 'info' },
      );
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to run health analysis', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, mergeFreshness]);

  const summary = result?.summary;
  const issues = result?.issues || [];

  return (
    <Box>
      <AnalyzerHeader
        catalog={catalog}
        loading={loading}
        onRun={runAnalysis}
        hasResult={!!result}
        rightContent={<NeighborDataChipConnected />}
      />

      {result && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <ScoreCard score={result.score} />
            </Box>
            <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Snapshot
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                <Chip size="small" label={`${summary?.total_devices ?? 0} devices`} />
                <Chip size="small" color="primary" label={`${summary?.devices_analyzed ?? 0} analysed`} />
                <Chip
                  size="small"
                  color={summary?.issues_found > 0 ? 'warning' : 'success'}
                  label={`${summary?.issues_found ?? 0} issues`}
                />
                <Chip
                  size="small"
                  color={summary?.offline_count > 0 ? 'error' : 'default'}
                  label={`${summary?.offline_count ?? 0} offline`}
                />
              </Stack>
            </Paper>
          </Stack>

          {issues.length === 0 ? (
            <Alert severity="success">No health issues detected — tree is clean.</Alert>
          ) : (
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Serial</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Kind</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Suggested next</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {issues.map((issue) => (
                      <TableRow key={issue.issue_id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {issue.serial}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {issue.kind === 'near_far_overpower_risk' ? (
                            <Tooltip title="Near-far over-power: this child is a detached far outlier on a parent that also serves a strong near cohort. Prefer reparenting it over raising parent TX.">
                              <Chip size="small" label="near-far over-power" color="secondary" variant="filled" />
                            </Tooltip>
                          ) : (
                            <Chip size="small" label={issue.kind} variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={issue.severity?.toFixed(2)}
                            color={severityColor(issue.severity)}
                          />
                        </TableCell>
                        <TableCell>{issue.reason}</TableCell>
                        <TableCell>
                          {issue.suggested ? (
                            <Tooltip title="Hint about which deeper analyser to run next">
                              <Chip size="small" label={issue.suggested} color="info" variant="outlined" />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {!result && !loading && (
        <Alert severity="info">
          Click <strong>Run</strong> to analyse current tree health. This is read-only — nothing is changed.
        </Alert>
      )}
    </Box>
  );
};

export default HealthPanel;
